import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { OUTLINE_CHANGED } from '../../hooks/useBookPath';
import { plotDossiersApi } from '../../api/plotDossiers';
import { getOutlineMap } from '../../api/storyBoard';
import { handmadeSequenceCount } from '../../utils/outlineCardRows';
import { parseStoryFrameworkRef, resolveStoryFrameworkMeta, storyFrameworkRef } from '../../api/frameworks';
import { frameworkHangCrowded, frameworkHangSparePx, hangModeAfterFlip, nextFrameworkHangMode } from '../../utils/frameworkHangFit';
import FrameworkPicker from './FrameworkPicker';
import FrameworkExplainerLink from './FrameworkExplainerLink';
import GenreStandingMark from './GenreStandingMark';
import ThinGenreMarker from './ThinGenreMarker';
import {
  FRAMEWORK_CHANGE,
  FRAMEWORK_NOW_THIS_BOOKS,
  FRAMEWORK_REMOVE,
  FRAMEWORK_REMOVE_BODY,
  FRAMEWORK_REMOVE_CONFIRM,
  FRAMEWORK_REMOVE_KEEP,
  FRAMEWORK_REMOVE_TITLE,
  FRAMEWORK_SAVE_ERROR,
  THIS_BECOMES_THIS_BOOKS_FRAMEWORK,
} from '../../utils/writerCopy';

export const BOOK_STRUCTURE_HINT = THIS_BECOMES_THIS_BOOKS_FRAMEWORK;
export const BOOK_STRUCTURE_CHOOSE = 'Choose a Framework';
export const BOOK_STRUCTURE_USE = 'Use this Framework';
// Run 40: the badge showed "custom:<id>" while the name loaded.
export const BOOK_STRUCTURE_SET = "This book's Framework";
// Run 46: "Choose framework" showed for a few seconds on a book that had one.
export const BOOK_STRUCTURE_LOADING = 'Framework';

const NO_FRAMEWORK_KEY = 'brainstormy.outline.noFramework.';

function rememberNoFramework(storyId, empty) {
  if (!storyId || typeof sessionStorage === 'undefined') return;
  const key = NO_FRAMEWORK_KEY + storyId;
  try {
    if (empty) sessionStorage.setItem(key, '1');
    else sessionStorage.removeItem(key);
  } catch {
    /* the label still follows this visit's answer */
  }
}

function rememberedNoFramework(storyId) {
  if (!storyId || typeof sessionStorage === 'undefined') return false;
  try {
    return sessionStorage.getItem(NO_FRAMEWORK_KEY + storyId) === '1';
  } catch {
    return false;
  }
}

function frameworkRefIsEmpty(value) {
  if (value == null) return true;
  const text = String(value).trim().toLowerCase();
  return text === '' || text === 'none';
}

function gapPx(style) {
  const raw = style.columnGap && style.columnGap !== 'normal' ? style.columnGap : style.gap;
  const value = Number.parseFloat(raw);
  return Number.isFinite(value) ? value : 0;
}

function textWidth(node) {
  if (!node || typeof document.createRange !== 'function') return 0;
  const range = document.createRange();
  range.selectNodeContents(node);
  const width = range.getBoundingClientRect().width || 0;
  if (typeof range.detach === 'function') range.detach();
  return width;
}

function usedWidth(parent) {
  if (!parent) return 0;
  const gap = gapPx(getComputedStyle(parent));
  const visible = [...parent.children].filter((el) => getComputedStyle(el).display !== 'none');
  const boxes = visible.reduce((sum, el) => sum + el.getBoundingClientRect().width, 0);
  return boxes + gap * Math.max(0, visible.length - 1);
}

function headerSparePx({ heading, hang, button, name, chevron }) {
  if (!heading || !hang || !button || !name) return 0;
  const buttonStyle = getComputedStyle(button);
  const pad = ['paddingLeft', 'paddingRight', 'borderLeftWidth', 'borderRightWidth']
    .reduce((sum, key) => sum + (Number.parseFloat(buttonStyle[key]) || 0), 0);
  return frameworkHangSparePx({
    headingClient: heading.clientWidth,
    headingUsed: usedWidth(heading),
    hangBox: hang.getBoundingClientRect().width,
    hangContent: usedWidth(hang),
    buttonBox: button.getBoundingClientRect().width,
    buttonContent: textWidth(name) + (chevron ? chevron.getBoundingClientRect().width : 0) + pad,
  });
}

/**
 * Book-level hang (#913 leftover of #908). Persists on the book via the
 * Plot Dossier framework slot (and stories.current_framework_key).
 * Not the Plot Focus disposable lens.
 */
export default function BookStructureDoor({
  storyId,
  open: openProp,
  onOpenChange,
  showTrigger = true,
  triggerClassName,
  fitTitle = false,
  onHung,
  frameworkKnown = false,
  initialFrameworkRef = null,
  sequences = null,
}) {
  const knownEmpty = frameworkKnown && frameworkRefIsEmpty(initialFrameworkRef);
  const [internalOpen, setInternalOpen] = useState(false);
  const [meta, setMeta] = useState(null);
  const [ref, setRef] = useState(null);
  // Empty outline already knows there is no Framework. Start on the choose
  // label so the first paint does not read the loading placeholder.
  const [loaded, setLoaded] = useState(knownEmpty);
  const [error, setError] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [titleMode, setTitleMode] = useState('roomy');
  const hangRef = useRef(null);
  const nameRef = useRef(null);
  const genreWidthRef = useRef(0);
  const hangHistoryRef = useRef([]);
  const hangLockRef = useRef(false);
  const hangNameRef = useRef('');
  const loadGen = useRef(0);
  const knownNameRef = useRef('');
  const [outlineSections, setOutlineSections] = useState(null);
  const controlled = openProp !== undefined;
  const isOpen = controlled ? Boolean(openProp) : internalOpen;

  const setOpen = useCallback((next) => {
    if (!controlled) setInternalOpen(next);
    onOpenChange?.(next);
  }, [controlled, onOpenChange]);

  const load = useCallback(async () => {
    const gen = loadGen.current + 1;
    loadGen.current = gen;
    if (!storyId) {
      setMeta(null);
      setRef(null);
      setLoaded(true);
      return;
    }
    try {
      const status = await plotDossiersApi.status(storyId);
      if (gen !== loadGen.current) return;
      const nextRef = status?.framework_ref || null;
      let nextMeta = null;
      if (nextRef) {
        try {
          nextMeta = await resolveStoryFrameworkMeta(nextRef);
        } catch {
          nextMeta = null;
        }
      }
      if (gen !== loadGen.current) return;
      // Name and reference land together. Setting the reference first
      // flashed "This book's Framework" before the real name.
      setRef(nextRef);
      setMeta(nextMeta);
      rememberNoFramework(storyId, !nextRef);
    } catch {
      if (gen !== loadGen.current) return;
      setMeta(null);
      setRef(null);
    } finally {
      if (gen === loadGen.current) setLoaded(true);
    }
  }, [storyId]);

  useEffect(() => {
    const emptyAlready = frameworkKnown && frameworkRefIsEmpty(initialFrameworkRef);
    // A book that already shows its Framework name must not drop back to
    // the loading label while a newer read is out.
    if (!knownNameRef.current && !rememberedNoFramework(storyId) && !emptyAlready) {
      setLoaded(false);
    }
    load();
  }, [load, storyId, frameworkKnown, initialFrameworkRef]);

  const loadOutlineSections = useCallback(async () => {
    if (sequences != null || !storyId) return null;
    try {
      const map = await getOutlineMap(storyId);
      const sections = map?.sections || [];
      setOutlineSections(sections);
      return sections;
    } catch {
      setOutlineSections([]);
      return [];
    }
  }, [sequences, storyId]);

  useEffect(() => {
    let cancelled = false;
    if (sequences != null || !storyId) {
      setOutlineSections(null);
      return undefined;
    }
    loadOutlineSections().then((sections) => {
      if (cancelled || sections == null) return;
      setOutlineSections(sections);
    });
    return () => { cancelled = true; };
  }, [loadOutlineSections, sequences, storyId]);

  useEffect(() => {
    if (!storyId || typeof window === 'undefined') return undefined;
    const onOutline = () => {
      load();
      loadOutlineSections();
    };
    window.addEventListener(OUTLINE_CHANGED, onOutline);
    return () => window.removeEventListener(OUTLINE_CHANGED, onOutline);
  }, [load, loadOutlineSections, storyId]);

  const handleSelect = async (dto, choice = {}) => {
    if (!storyId) return;
    setError(null);
    const keepParts = Boolean(choice.keepParts);
    try {
      const saved = keepParts
        ? await plotDossiersApi.setFramework(storyId, storyFrameworkRef(dto), { keepParts: true })
        : await plotDossiersApi.setFramework(storyId, storyFrameworkRef(dto));
      setConfirmation(FRAMEWORK_NOW_THIS_BOOKS(dto?.framework_name || 'This Framework'));
      // Close the picker, then refresh. The outline grid stays mounted;
      // onHung reloads it quietly instead of swapping in a blank page.
      setOpen(false);
      await onHung?.(dto, {
        previousFrameworkRef: saved?.previous_framework_ref ?? null,
      });
      await load();
      await loadOutlineSections();
      return true;
    } catch (err) {
      setError(err?.message || FRAMEWORK_SAVE_ERROR);
    }
  };

  const knownName = (meta?.framework_name || '').trim();
  knownNameRef.current = knownName;
  let label = BOOK_STRUCTURE_CHOOSE;
  if (knownName) label = knownName;
  else if (!loaded) {
    label = (rememberedNoFramework(storyId) && !ref)
      ? BOOK_STRUCTURE_CHOOSE
      : BOOK_STRUCTURE_LOADING;
  } else if (ref) label = BOOK_STRUCTURE_SET;
  const tight = fitTitle && titleMode === 'tight';

  useLayoutEffect(() => {
    if (!fitTitle) return undefined;
    const hang = hangRef.current;
    if (!hang || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => {
      const name = nameRef.current;
      if (!name || menuOpen) return;
      const heading = hang.parentElement;
      const genre = hang.querySelector('.fw-detail__tag');
      const button = hang.querySelector('button');
      const chevron = hang.querySelector('.fw-book-hang__chevron');
      const rowOverflow = frameworkHangCrowded({
        hangScroll: hang.scrollWidth,
        hangClient: hang.clientWidth,
        buttonScroll: button ? button.scrollWidth : 0,
        buttonClient: button ? button.clientWidth : 0,
      });
      const nameOverflow = name.scrollWidth > name.clientWidth + 1;
      let genrePx = genreWidthRef.current;
      if (genre && titleMode !== 'tight') {
        genrePx = genre.getBoundingClientRect().width || genre.scrollWidth || 0;
        genreWidthRef.current = genrePx;
      }
      const sparePx = headerSparePx({ heading, hang, button, name, chevron });
      const hangGap = gapPx(getComputedStyle(hang));
      if (hangNameRef.current !== knownName) {
        hangNameRef.current = knownName;
        hangHistoryRef.current = [];
        hangLockRef.current = false;
      }
      // The mark also needs the gap beside the name, or a row that is
      // one pixel short toggles forever.
      const genreNeed = genrePx > 0 ? genrePx + hangGap : 0;
      const proposed = nextFrameworkHangMode(titleMode, {
        rowOverflow,
        nameOverflow,
        sparePx,
        genrePx: genreNeed,
      });
      const decision = hangModeAfterFlip(hangHistoryRef.current, proposed, {
        sparePx,
        genrePx: genreNeed,
        locked: hangLockRef.current,
      });
      hangHistoryRef.current = decision.history;
      hangLockRef.current = decision.locked;
      if (decision.mode !== titleMode) setTitleMode(decision.mode);
    };
    const observer = new ResizeObserver(measure);
    observer.observe(hang);
    measure();
    return () => observer.disconnect();
  }, [fitTitle, knownName, menuOpen, titleMode]);

  const removeFramework = async () => {
    if (!storyId) return;
    setError(null);
    try {
      await plotDossiersApi.setFramework(storyId, null);
      setConfirmRemove(false);
      setMenuOpen(false);
      await load();
      onHung?.(null);
    } catch (err) {
      setError(err?.message || FRAMEWORK_SAVE_ERROR);
    }
  };

  const openPicker = () => {
    setMenuOpen(false);
    if (sequences == null) void loadOutlineSections();
    setOpen(true);
  };

  const onTrigger = () => {
    if (ref) setMenuOpen((open) => !open);
    else openPicker();
  };

  // The Outline header fits the title into the row. That fit must not
  // hide the explainer: the link sits next to Choose a Framework once
  // the readiness rule is met and this book has no Framework.
  const showExplainer = showTrigger && loaded && !ref;

  return (
    <div
      className={tight ? 'fw-book-hang is-tight' : 'fw-book-hang'}
      ref={hangRef}
    >
      {showExplainer && (
        <FrameworkExplainerLink storyId={storyId} />
      )}
      {showTrigger && (
        <button
          type="button"
          className={triggerClassName || 'fw-btn fw-btn--ghost'}
          onClick={onTrigger}
          disabled={!storyId}
          aria-label={label}
          title={knownName || undefined}
          aria-haspopup={ref ? 'menu' : 'dialog'}
          aria-expanded={ref ? menuOpen : undefined}
        >
          {ref && knownName ? (
            <>
              <span className="fw-book-hang__name" ref={nameRef}>{label}</span>
              <span className="fw-book-hang__chevron" aria-hidden="true">{'\u00A0▾'}</span>
            </>
          ) : label}
        </button>
      )}
      {menuOpen && ref && (
        <div className="fw-book-hang__menu" role="menu" aria-label="Framework">
          <button
            type="button"
            role="menuitem"
            className="fw-book-hang__item"
            onClick={() => { openPicker(); }}
          >
            {FRAMEWORK_CHANGE}
          </button>
          <button
            type="button"
            role="menuitem"
            className="fw-book-hang__item"
            onClick={() => { setMenuOpen(false); setConfirmRemove(true); }}
          >
            {FRAMEWORK_REMOVE}
          </button>
        </div>
      )}
      {confirmRemove && (
        <div className="fw-book-hang__confirm" role="dialog" aria-modal="true" aria-labelledby="fw-remove-title">
          <h2 id="fw-remove-title">{FRAMEWORK_REMOVE_TITLE}</h2>
          <p>{FRAMEWORK_REMOVE_BODY}</p>
          <div className="fw-book-hang__confirm-actions">
            <button type="button" onClick={() => setConfirmRemove(false)}>{FRAMEWORK_REMOVE_KEEP}</button>
            <button type="button" onClick={removeFramework}>{FRAMEWORK_REMOVE_CONFIRM}</button>
          </div>
        </div>
      )}
      {!tight && <GenreStandingMark genre={ref ? meta?.genre_tag : null} />}
      <ThinGenreMarker show={Boolean(ref && meta?.thin_genre)} />
      {confirmation && <p className="fw-notice" role="status">{confirmation}</p>}
      {error && <p className="fw-error" role="alert">{error}</p>}
      <FrameworkPicker
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        mode="select"
        hangKind="book"
        storyId={storyId}
        selectLabel={BOOK_STRUCTURE_USE}
        selectHint={BOOK_STRUCTURE_HINT}
        selectedId={parseStoryFrameworkRef(ref)?.id || null}
        handmadeSequenceCount={sequences != null
          ? handmadeSequenceCount(sequences)
          : handmadeSequenceCount(
            (outlineSections || []).filter((row) => row?.section_type === 'sequence'),
            (outlineSections || []).filter((row) => row?.section_type === 'scene'),
          )}
        onSelect={handleSelect}
      />
    </div>
  );
}
